import React from "react";
import { restoreElements } from "@excalidraw/excalidraw";
import { v4 as uuidv4 } from "uuid";

// ── element builder helpers ─────────────────────────────────────────────────
const eid = () => uuidv4();

const mkRect = (x, y, w, h, { bg = "transparent", stroke = "#1e1e1e", fill = "solid", sw = 2, round = true } = {}) => ({
  type: "rectangle", id: eid(), x, y, width: w, height: h,
  strokeColor: stroke, backgroundColor: bg, fillStyle: fill, strokeWidth: sw,
  strokeStyle: "solid", roughness: 0, opacity: 100, angle: 0,
  roundness: round ? { type: 3 } : null,
  groupIds: [], boundElements: null, isDeleted: false, frameId: null,
});

const mkText = (x, y, t, { size = 14, bold = false, align = "left", color = "#1e1e1e", w = 300, h } = {}) => ({
  type: "text", id: eid(), x, y, width: w, height: h || size * 1.5,
  text: t, fontSize: size, fontFamily: bold ? 2 : 1,
  textAlign: align, verticalAlign: "top",
  strokeColor: color, backgroundColor: "transparent", fillStyle: "hachure",
  strokeWidth: 1, strokeStyle: "solid", roughness: 0, opacity: 100, angle: 0,
  groupIds: [], boundElements: null, isDeleted: false, frameId: null,
  containerId: null, originalText: t, lineHeight: 1.25,
});

const mkEllipse = (x, y, w, h, { bg = "transparent", stroke = "#1e1e1e", sw = 2 } = {}) => ({
  type: "ellipse", id: eid(), x, y, width: w, height: h,
  strokeColor: stroke, backgroundColor: bg, fillStyle: "solid", strokeWidth: sw,
  strokeStyle: "solid", roughness: 0, opacity: 100, angle: 0,
  roundness: { type: 2 }, groupIds: [], boundElements: null, isDeleted: false, frameId: null,
});

const mkLine = (x1, y1, x2, y2, { color = "#868e96", sw = 2 } = {}) => ({
  type: "line", id: eid(), x: x1, y: y1,
  width: Math.abs(x2 - x1), height: Math.abs(y2 - y1),
  points: [[0, 0], [x2 - x1, y2 - y1]],
  strokeColor: color, backgroundColor: "transparent", fillStyle: "hachure",
  strokeWidth: sw, strokeStyle: "solid", roughness: 0, opacity: 100, angle: 0,
  roundness: null, groupIds: [], boundElements: null, isDeleted: false, frameId: null,
  lastCommittedPoint: null, startBinding: null, endBinding: null,
  startArrowhead: null, endArrowhead: null,
});

const mkArrow = (x1, y1, x2, y2, { color = "#868e96", sw = 2 } = {}) => ({
  type: "arrow", id: eid(), x: x1, y: y1,
  width: Math.abs(x2 - x1), height: Math.abs(y2 - y1),
  points: [[0, 0], [x2 - x1, y2 - y1]],
  strokeColor: color, backgroundColor: "transparent", fillStyle: "hachure",
  strokeWidth: sw, strokeStyle: "solid", roughness: 0, opacity: 100, angle: 0,
  startArrowhead: null, endArrowhead: "arrow",
  roundness: { type: 2 }, groupIds: [], boundElements: null, isDeleted: false, frameId: null,
  lastCommittedPoint: null, startBinding: null, endBinding: null,
});

// ── template functions ──────────────────────────────────────────────────────

function kwlChart() {
  const els = [];
  const colW = 380, gap = 10, hdrH = 44, bodyH = 380, x0 = 20, y0 = 20;
  const headers = ["K — What I Know", "W — Want to Know", "L — What I Learned"];
  const bgs     = ["#e7f5ff", "#fff9db", "#ebfbee"];
  const strokes = ["#1971c2", "#e67700", "#2b8a3e"];
  const totalW  = colW * 3 + gap * 2;

  els.push(mkRect(x0, y0, totalW, 40, { bg: "#343a40", stroke: "#343a40", round: false }));
  els.push(mkText(x0, y0 + 9, "KWL Chart", { size: 18, bold: true, color: "#fff", align: "center", w: totalW }));

  headers.forEach((h, i) => {
    const x = x0 + i * (colW + gap);
    const y = y0 + 44;
    els.push(mkRect(x, y, colW, hdrH, { bg: strokes[i], stroke: strokes[i], round: false }));
    els.push(mkText(x + 8, y + 12, h, { size: 14, bold: true, color: "#fff", w: colW - 16 }));
    els.push(mkRect(x, y + hdrH, colW, bodyH, { bg: bgs[i], stroke: strokes[i] }));
  });
  return els;
}

function tChart() {
  const els = [];
  const x0 = 20, y0 = 20, totalW = 900, hdrH = 50, bodyH = 420;
  const colW = (totalW - 10) / 2;
  const titles  = ["Item / Concept A", "Item / Concept B"];
  const bgs     = ["#e7f5ff", "#fff9db"];
  const strokes = ["#1971c2", "#e67700"];

  els.push(mkRect(x0, y0, totalW, 40, { bg: "#343a40", stroke: "#343a40", round: false }));
  els.push(mkText(x0, y0 + 9, "T-Chart — Compare & Contrast", { size: 16, bold: true, color: "#fff", align: "center", w: totalW }));

  titles.forEach((t, i) => {
    const x = x0 + i * (colW + 10);
    const y = y0 + 44;
    els.push(mkRect(x, y, colW, hdrH, { bg: strokes[i], stroke: strokes[i], round: false }));
    els.push(mkText(x + 8, y + 14, t, { size: 15, bold: true, color: "#fff", w: colW - 16 }));
    els.push(mkRect(x, y + hdrH, colW, bodyH, { bg: bgs[i], stroke: strokes[i] }));
  });
  return els;
}

function exitTicket() {
  const els = [];
  const x0 = 20, y0 = 20, w = 800, rowH = 160, gap = 10;
  const items = [
    ["3", "Three things I learned today...", "#1971c2", "#e7f5ff"],
    ["2", "Two things I found interesting...", "#e67700", "#fff9db"],
    ["1", "One question I still have...", "#2b8a3e", "#ebfbee"],
  ];

  els.push(mkRect(x0, y0, w, 44, { bg: "#5c2d91", stroke: "#5c2d91", round: false }));
  els.push(mkText(x0, y0 + 11, "Exit Ticket — 3-2-1", { size: 18, bold: true, color: "#fff", align: "center", w }));

  items.forEach(([num, label, stroke, bg], i) => {
    const y = y0 + 56 + i * (rowH + gap);
    els.push(mkRect(x0, y, w, rowH, { bg, stroke }));
    els.push(mkText(x0 + 16, y + rowH / 2 - 28, num, { size: 56, bold: true, color: stroke, w: 70 }));
    els.push(mkText(x0 + 90, y + 16, label, { size: 14, color: "#1e1e1e", w: w - 110 }));
  });
  return els;
}

function cornellNotes() {
  const els = [];
  const x0 = 20, y0 = 20, totalW = 1000, noteH = 500;
  const cueW = 260, mainW = 720, gap = 10, summH = 120;

  els.push(mkRect(x0, y0, totalW, 44, { bg: "#343a40", stroke: "#343a40", round: false }));
  els.push(mkText(x0, y0 + 11, "Cornell Notes", { size: 18, bold: true, color: "#fff", align: "center", w: totalW }));

  const y1 = y0 + 44;
  // Cues column
  els.push(mkRect(x0, y1, cueW, 36, { bg: "#1971c2", stroke: "#1971c2", round: false }));
  els.push(mkText(x0 + 8, y1 + 8, "Cues / Questions", { size: 13, bold: true, color: "#fff", w: cueW - 16 }));
  els.push(mkRect(x0, y1 + 36, cueW, noteH - 36, { bg: "#e7f5ff", stroke: "#1971c2" }));

  // Main notes
  const xMain = x0 + cueW + gap;
  els.push(mkRect(xMain, y1, mainW, 36, { bg: "#343a40", stroke: "#343a40", round: false }));
  els.push(mkText(xMain + 8, y1 + 8, "Notes", { size: 13, bold: true, color: "#fff", w: mainW - 16 }));
  els.push(mkRect(xMain, y1 + 36, mainW, noteH - 36, { bg: "#f8f9fa", stroke: "#343a40" }));

  // Summary
  const ySumm = y1 + noteH + gap;
  els.push(mkRect(x0, ySumm, totalW, 36, { bg: "#5c2d91", stroke: "#5c2d91", round: false }));
  els.push(mkText(x0 + 8, ySumm + 8, "Summary", { size: 13, bold: true, color: "#fff", w: 200 }));
  els.push(mkRect(x0, ySumm + 36, totalW, summH, { bg: "#f3f0ff", stroke: "#5c2d91" }));
  return els;
}

function mindMap() {
  const els = [];
  const cx = 500, cy = 350, cW = 200, cH = 80;

  // Center
  els.push(mkEllipse(cx - cW / 2, cy - cH / 2, cW, cH, { bg: "#1971c2", stroke: "#1971c2" }));
  els.push(mkText(cx - 70, cy - 12, "Central Idea", { size: 15, bold: true, color: "#fff", align: "center", w: 140 }));

  const nodes = [
    { label: "Branch 1", angle: 0   },
    { label: "Branch 2", angle: 60  },
    { label: "Branch 3", angle: 120 },
    { label: "Branch 4", angle: 180 },
    { label: "Branch 5", angle: 240 },
    { label: "Branch 6", angle: 300 },
  ];
  const radius = 240, nW = 160, nH = 60;
  const colors = ["#2b8a3e", "#e67700", "#c2255c", "#5c2d91", "#0b7285", "#862e9c"];

  nodes.forEach(({ label, angle }, i) => {
    const rad = (angle * Math.PI) / 180;
    const nx  = cx + radius * Math.cos(rad);
    const ny  = cy + radius * Math.sin(rad);
    els.push(mkLine(cx, cy, nx, ny, { color: colors[i], sw: 2 }));
    els.push(mkEllipse(nx - nW / 2, ny - nH / 2, nW, nH, { bg: colors[i] + "33", stroke: colors[i] }));
    els.push(mkText(nx - nW / 2 + 8, ny - 10, label, { size: 13, bold: true, color: colors[i], align: "center", w: nW - 16 }));
  });
  return els;
}

function timeline() {
  const els = [];
  const x0 = 60, y0 = 40, lineY = 220, evCount = 5;
  const totalW = 1100;
  const gap = (totalW - x0 * 2) / (evCount - 1);

  els.push(mkText(x0, y0, "Timeline", { size: 20, bold: true, align: "center", w: totalW - x0 * 2, color: "#1e1e1e" }));
  // Main arrow
  els.push(mkArrow(x0, lineY, x0 + totalW - x0, lineY, { color: "#343a40", sw: 3 }));

  const evColors = ["#1971c2", "#2b8a3e", "#e67700", "#c2255c", "#5c2d91"];

  for (let i = 0; i < evCount; i++) {
    const ex    = x0 + i * gap;
    const isTop = i % 2 === 0;
    const boxH  = 70;
    const boxY  = isTop ? lineY - 130 : lineY + 44;
    const tickY1 = isTop ? lineY - 8 : lineY + 8;
    const tickY2 = isTop ? boxY + boxH : boxY;

    els.push(mkLine(ex, tickY1, ex, tickY2, { color: evColors[i], sw: 2 }));
    els.push(mkEllipse(ex - 8, lineY - 8, 16, 16, { bg: evColors[i], stroke: evColors[i] }));
    els.push(mkRect(ex - 80, boxY, 160, boxH, { bg: evColors[i] + "22", stroke: evColors[i] }));
    els.push(mkText(ex - 72, boxY + 8, `Event ${i + 1}`, { size: 13, bold: true, color: evColors[i], w: 144 }));
    els.push(mkText(ex - 72, boxY + 30, "Date / Description", { size: 11, color: "#868e96", w: 144 }));
  }
  return els;
}

function quizBoard() {
  const els = [];
  const x0 = 20, y0 = 20, totalW = 1020, qW = 320, qH = 130, gap = 10;
  const colors  = ["#e7f5ff", "#fff9db", "#ebfbee", "#f3f0ff", "#fff4e6", "#fce4ec"];
  const strokes = ["#1971c2", "#e67700", "#2b8a3e", "#5c2d91", "#d9480f", "#c2255c"];

  els.push(mkRect(x0, y0, totalW, 50, { bg: "#1971c2", stroke: "#1971c2", round: false }));
  els.push(mkText(x0, y0 + 13, "Quiz Board", { size: 20, bold: true, color: "#fff", align: "center", w: totalW }));

  for (let i = 0; i < 6; i++) {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x   = x0 + col * (qW + gap);
    const y   = y0 + 60 + row * (qH + gap);
    els.push(mkRect(x, y, qW, qH, { bg: colors[i], stroke: strokes[i] }));
    els.push(mkText(x + 12, y + 12, `Q${i + 1}.`, { size: 14, bold: true, color: strokes[i], w: 40 }));
    els.push(mkText(x + 54, y + 14, "Type your question here...", { size: 13, color: "#495057", w: qW - 68 }));
  }
  return els;
}

function lessonPlan() {
  const els = [];
  const sections = [
    { label: "Learning Objectives", x: 20,  y: 110, w: 420, h: 200, stroke: "#1971c2", bg: "#e7f5ff" },
    { label: "Key Vocabulary",      x: 460, y: 110, w: 300, h: 200, stroke: "#2b8a3e", bg: "#ebfbee" },
    { label: "Starter Activity",    x: 780, y: 110, w: 600, h: 200, stroke: "#e67700", bg: "#fff9db" },
    { label: "Main Explanation",    x: 20,  y: 350, w: 860, h: 250, stroke: "#5c2d91", bg: "#f3f0ff" },
    { label: "Worked Example",      x: 900, y: 350, w: 480, h: 250, stroke: "#0b7285", bg: "#e3fafc" },
    { label: "Student Task",        x: 20,  y: 640, w: 860, h: 210, stroke: "#c2255c", bg: "#fff0f6" },
    { label: "Plenary / Exit Ticket", x: 900, y: 640, w: 480, h: 210, stroke: "#e67700", bg: "#fff9db" },
    { label: "Success Criteria",    x: 20,  y: 890, w: 1360, h: 90,  stroke: "#343a40", bg: "#f8f9fa" },
  ];

  els.push(mkRect(20, 20, 1360, 70, { bg: "#1971c2", stroke: "#1971c2" }));
  els.push(mkText(20, 35, "Lesson Plan", { size: 26, bold: true, color: "#fff", align: "center", w: 1360 }));

  sections.forEach(({ label, x, y, w, h, stroke, bg }) => {
    els.push(mkRect(x, y, w, h, { bg, stroke, sw: 2 }));
    els.push(mkText(x + 6, y - 20, label, { size: 12, bold: true, color: stroke, w: w - 12 }));
  });
  return els;
}

function venDiagram() {
  const els = [];
  const x0 = 20, y0 = 20, r = 200;
  const cx1 = 200, cx2 = 480, cy = 280;

  els.push(mkText(x0, y0, "Venn Diagram", { size: 20, bold: true, color: "#1e1e1e", w: 640 }));
  els.push(mkText(x0, y0 + 30, "Topic / Label A", { size: 14, bold: true, color: "#1971c2", w: 200 }));
  els.push(mkText(460, y0 + 30, "Topic / Label B", { size: 14, bold: true, color: "#c2255c", w: 200 }));
  els.push(mkText(270, y0 + 30, "Both", { size: 14, bold: true, color: "#2b8a3e", align: "center", w: 100 }));

  // Left circle
  els.push(mkEllipse(cx1 - r, cy - r, r * 2, r * 2, { bg: "#1971c233", stroke: "#1971c2", sw: 2 }));
  // Right circle
  els.push(mkEllipse(cx2 - r, cy - r, r * 2, r * 2, { bg: "#c2255c33", stroke: "#c2255c", sw: 2 }));
  return els;
}

function fourCorners() {
  const els = [];
  const x0 = 20, y0 = 20, totalW = 900, totalH = 640, half = (totalW - 10) / 2, halfH = (totalH - 10) / 2;
  const labels   = ["Strengths", "Weaknesses", "Opportunities", "Threats"];
  const bgs      = ["#ebfbee", "#fff4e6", "#e7f5ff", "#fff9db"];
  const strokes  = ["#2b8a3e", "#d9480f", "#1971c2", "#e67700"];
  const positions= [[x0, y0+40], [x0+half+10, y0+40], [x0, y0+40+halfH+10], [x0+half+10, y0+40+halfH+10]];

  els.push(mkRect(x0, y0, totalW, 36, { bg: "#343a40", stroke: "#343a40", round: false }));
  els.push(mkText(x0, y0 + 8, "SWOT Analysis", { size: 16, bold: true, color: "#fff", align: "center", w: totalW }));

  labels.forEach((label, i) => {
    const [px, py] = positions[i];
    els.push(mkRect(px, py, half, halfH, { bg: bgs[i], stroke: strokes[i] }));
    els.push(mkText(px + 10, py + 12, label, { size: 16, bold: true, color: strokes[i], w: half - 20 }));
  });
  return els;
}

// ── template registry ───────────────────────────────────────────────────────
const TEMPLATES = [
  { id: "lesson",   icon: "📋", name: "Lesson Plan",        desc: "Full lesson layout: objectives, tasks & plenary", fn: lessonPlan  },
  { id: "kwl",      icon: "🧠", name: "KWL Chart",          desc: "What I Know / Want to Know / Learned",            fn: kwlChart    },
  { id: "tchart",   icon: "⚖️",  name: "T-Chart",            desc: "Side-by-side comparison of two concepts",         fn: tChart      },
  { id: "exit",     icon: "🎫", name: "Exit Ticket (3-2-1)",desc: "3 learned · 2 interesting · 1 question",          fn: exitTicket  },
  { id: "cornell",  icon: "📓", name: "Cornell Notes",      desc: "Cues, main notes area, and summary row",          fn: cornellNotes},
  { id: "mindmap",  icon: "🕸️",  name: "Mind Map",           desc: "Central idea with 6 surrounding branches",        fn: mindMap     },
  { id: "timeline", icon: "📅", name: "Timeline",           desc: "5-event horizontal timeline with boxes",          fn: timeline    },
  { id: "quiz",     icon: "❓", name: "Quiz Board",         desc: "6 colourful question boxes for class quizzes",    fn: quizBoard   },
  { id: "venn",     icon: "🔵", name: "Venn Diagram",       desc: "Two overlapping circles for comparison",          fn: venDiagram  },
  { id: "swot",     icon: "🔲", name: "SWOT Analysis",      desc: "Strengths, Weaknesses, Opportunities, Threats",   fn: fourCorners },
];

// ── component ───────────────────────────────────────────────────────────────
const WhiteboardTemplates = ({ excalidrawAPI, onClose }) => {
  const handleSelect = (tpl) => {
    if (!excalidrawAPI) return;
    const doReplace = window.confirm(
      `Load template "${tpl.name}"?\n\n` +
      `OK     → Replace the current board\n` +
      `Cancel → Add alongside existing content`
    );
    const raw      = tpl.fn();
    const restored = restoreElements(raw, null);

    if (doReplace) {
      excalidrawAPI.updateScene({ elements: restored });
    } else {
      // Shift new elements 1600 px to the right so they don't overlap
      const shifted = restored.map(el => ({ ...el, x: (el.x || 0) + 1600 }));
      const reRestored = restoreElements(shifted, null);
      excalidrawAPI.updateScene({
        elements: [...excalidrawAPI.getSceneElements(), ...reRestored],
      });
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-2xl flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-800">📐 Whiteboard Templates</h2>
            <p className="text-xs text-gray-500 mt-0.5">Click a template · OK replaces board · Cancel adds alongside</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-3xl leading-none font-light"
          >
            &times;
          </button>
        </div>

        {/* Grid */}
        <div className="overflow-y-auto p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {TEMPLATES.map(tpl => (
              <button
                key={tpl.id}
                onClick={() => handleSelect(tpl)}
                className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all text-center group"
              >
                <span className="text-4xl">{tpl.icon}</span>
                <span className="font-semibold text-sm text-gray-800 group-hover:text-blue-700 leading-tight">
                  {tpl.name}
                </span>
                <span className="text-xs text-gray-400 leading-snug">{tpl.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhiteboardTemplates;
